import {
  PatientEncounter,
} from '../../../../patient/components/models/patient-encounter';
import {GuestToken} from '../../../../security/services/SSO';
import {
  ApplicationStatusState,
  GetError,
} from '../../../../security/model/application-status';
import React, {useState} from 'react';
import HttpInterceptor from '../../../../shared/Http/http-interceptor';
import {patientService} from '../../../../patient/services/patient-service';
import {
  specialistClientService,
} from '../../../../implementation-specialist/services/implementation-client-service';
import {
  AuthorizationDocumentStatus,
} from '../payment-program-enrollment/payment-program-enrollment-page';
import {getDynamicContent} from '../no-sig-dynamic-content';
import {ApprovalView} from '../approval-view';
import {getMethodApprovedFromSearch} from '../get-method-approved';
import {
  commentsService
} from '@finpay-development/shared-platform/lib/esm/admin/services/comments-service';
import {GENERIC_ERROR_MSG} from '../error-messages';
import {paynowService} from '../../../services/paynow-service';

interface EstimateApprovalViewProps {
  patientEncounter: PatientEncounter | null;
  selectedAuthDocStatus: AuthorizationDocumentStatus | null;
  guestToken: GuestToken | null;
  setMessage: (msg: ApplicationStatusState) => void;
  authDocPdfBlob: Blob | null;
  loadErrorMsg?: string;
}

export const EstimateApprovalView = (props: EstimateApprovalViewProps) => {

  const {
    patientEncounter,
    guestToken,
    selectedAuthDocStatus,
    setMessage,
    authDocPdfBlob,
    loadErrorMsg,
  } = props;

  const [modalOpen, setModalOpen] = useState(false);
  const [isThankYou, setIsThankYou] = useState(false);
  const [clientDetails, setClientDetails] = useState({
    surveyUrl: '',
    clientName: '',
    email: '',
    phoneNumber: '',
  });

  const handleApproveBtnOnClick = async () => {

    HttpInterceptor(false, guestToken!, true);

    const resolvedPromises = await Promise.all([
      processAuthDocApproval({
        authorizationDocumentStatusId: selectedAuthDocStatus?.authorizationDocumentStatusId!,
        patientDocumentId: selectedAuthDocStatus?.patientDocument?.patientDocumentId,
        patientId: patientEncounter!.patientId,
        patientEncounterId: patientEncounter!.patientEncounterId,
        estimateId: patientEncounter!.pfrEstimateId!,
        authToken: guestToken!.token,
      }),
      specialistClientService.getClient(patientEncounter!.clientId),
    ]);

    for (let i = 0; i < resolvedPromises.length; i++) {
      if (resolvedPromises?.[i]?.hasErrors) {
        setMessage(GetError(GENERIC_ERROR_MSG));
        return;
      }
    }

    const fetchedClientDetail = resolvedPromises[1]?.entity?.fullClient;

    setIsThankYou(true);
    setClientDetails({
      ...clientDetails,
      clientName: fetchedClientDetail?.clientName,
      email: fetchedClientDetail?.primaryContact?.email,
      phoneNumber: fetchedClientDetail?.primaryContact?.primPhoneNum,
    });

  };

  const typeOfDoc = selectedAuthDocStatus?.authorizationDocuments?.typeOfDoc;
  const dynamicContent = patientEncounter && typeOfDoc
      ? getDynamicContent({
        typeOfDoc,
        patientEncounter,
        modalState: {
          state: modalOpen,
          setState: setModalOpen,
        },
        pdfBlob: authDocPdfBlob,
      })
      : { estimateDetails: <></>, docModal: <></> };

  const clientName = patientEncounter?.client?.clientName || '';
  const facilityName = patientEncounter?.facilityName || '';
  const checkboxLabel =
      `I agree to be financially responsible for the services received by ${clientName} at ${facilityName}. ` +
      `I understand that this is an estimate based on my current insurance benefits and the information available at this time. ` +
      `I understand that my final responsibility may be different depending on how claims are processed, including but not limited to actual services received, medical necessity, benefit coverage, coordination of benefits, and any changes to my insurance plan. ` +
      `I agree to pay any remaining balance for which I am responsible.`;

  return (
      <ApprovalView
          pageContent={{
            title: 'Financial Responsibility Agreement',
            body: {
              intro: '',
              details: dynamicContent?.estimateDetails!,
              explanation: '',
              instructions: 'to see a breakdown of your estimate.',
              checkboxLabel,
            },
            footerNotes: [],
          }}
          docModal={dynamicContent.docModal}
          displayThankYouView={isThankYou}
          clientDetails={clientDetails}
          modalOpenState={[modalOpen, setModalOpen]}
          setMessage={setMessage}
          handleApprovedBtnOnClick={handleApproveBtnOnClick}
          blockedApprovalError={loadErrorMsg}
          thankYouViewConfig={{
            thankYouText: 'Thank you for completing your agreement.',
            enableSurvey: false,
          }}
      />
  );

};

const revertAuthDocToSent = async (authorizationDocumentStatusId: number) => {
  await patientService.updateAuthorizationDocumentStatus({
    dataToSave: {
      documentStatus: 'sent',
      dateApproved: null,
    },
    authorizationDocumentStatusId,
  });
};

const processAuthDocApproval = async (params: {
  authorizationDocumentStatusId: number
  patientDocumentId?: number
  patientId: number
  patientEncounterId: number
  estimateId: number
  authToken: string
}) => {

  const {
    authorizationDocumentStatusId,
    patientDocumentId,
    patientId,
    patientEncounterId,
    estimateId,
    authToken,
  } = params;
  const methodApproved = getMethodApprovedFromSearch();

  if (!patientDocumentId) {
    return {hasErrors: true};
  }

  // Mark signed first so estimation can merge the PES no-sig approval page
  // when authorizationDocumentStatusId is sent.
  const authDocResponse = await patientService.updateAuthorizationDocumentStatus(
      {
        dataToSave: {
          documentStatus: 'signed',
          dateApproved: new Date().toISOString(),
          ...methodApproved && {methodApproved},
        },
        authorizationDocumentStatusId: authorizationDocumentStatusId,
      });

  if (authDocResponse?.hasErrors) {
    return authDocResponse;
  }

  const uploadResponse = await paynowService.postApi(
      'POST',
      `/estimation/v3/estimate/${estimateId}/estimateAuthDocument?isUpload=true`,
      {authorizationDocumentStatusId},
      authToken,
  );

  // estimateAuthDocument?isUpload=true always returns S3 storage metadata.
  const storage = uploadResponse?.data?.storage;
  const uploadedS3Key = storage?.storageMetadata?.s3?.key;

  // Persist uploaded storage metadata on the patient document.
  const persistResponse = uploadedS3Key
      ? await paynowService.postApi(
          'PATCH',
          `/patient-encounter/v2/patient/encounter/${patientEncounterId}/document/${patientDocumentId}`,
          {
            storageProvider: storage.storageProvider,
            storageMetadata: storage.storageMetadata,
          },
          authToken,
      )
      : null;

  if (!uploadedS3Key || !persistResponse || persistResponse.hasErrors) {
    await revertAuthDocToSent(authorizationDocumentStatusId);
    return {hasErrors: true};
  }

  await commentsService.createPatientNote({
    patientId,
    patientEncounterId,
    noteText: `Estimate agreement has been approved.`,
    noteDt: new Date().toISOString()
  });
};
